import { ChevronDown, ChevronRight, Loader2, Plus, Trash2, Volume2 } from 'lucide-react';
import { useState } from 'react';

import { api } from '@/lib/api';
import type { Segment } from '@/lib/schema.types';
import { cn } from '@/lib/utils';

interface Props {
  segments: Segment[];
  onChange: (next: Segment[]) => void;
  /** Shown above the list when empty. */
  emptyLabel?: string;
  /** Compact mode hides board/visualRef/imageUrl behind a disclosure. */
  compact?: boolean;
}

/**
 * Editable list of `Segment` records — what Electron plays back when a
 * reteach / expandedSegments / anticipatedDoubt is triggered. We keep the
 * editor minimal for the first cut: every segment shows `type` and `speech`
 * inline; `board` / `visualRef` / `imageUrl` are tucked behind a disclosure
 * so the list stays scannable.
 */
export function SegmentList({ segments, onChange, emptyLabel, compact = true }: Props) {
  const addEmpty = () =>
    onChange([...segments, { type: 'explanation', speech: '' }]);

  return (
    <div className="space-y-2">
      {segments.length === 0 && emptyLabel && (
        <p className="rounded-md border border-dashed border-border bg-bg-subtle/40 px-3 py-2 text-[11.5px] text-fg-subtle">
          {emptyLabel}
        </p>
      )}

      {segments.map((seg, i) => (
        <SegmentRow
          key={i}
          segment={seg}
          compact={compact}
          onChange={(next) => onChange(segments.map((s, ix) => (ix === i ? next : s)))}
          onRemove={() => onChange(segments.filter((_, ix) => ix !== i))}
        />
      ))}

      <button
        type="button"
        onClick={addEmpty}
        className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-white px-2 py-1.5 text-[11.5px] font-medium text-fg-muted transition hover:border-accent/60 hover:text-accent"
      >
        <Plus size={11} /> Add segment
      </button>
    </div>
  );
}

function SegmentRow({
  segment,
  compact,
  onChange,
  onRemove,
}: {
  segment: Segment;
  compact: boolean;
  onChange: (next: Segment) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(!compact);
  const [busy, setBusy] = useState(false);
  const [audioErr, setAudioErr] = useState<string | null>(null);

  const speech = (segment.speech ?? '').trim();
  const aud = segment.audio;
  const stale =
    !!aud?.src && !!aud.audioForText && aud.audioForText.trim() !== speech;

  const generateAudio = async () => {
    if (!speech) return;
    setBusy(true);
    setAudioErr(null);
    try {
      // Same VoxCPM path as reveal-step narration; the spoken text is `speech`.
      const res = await api.ai.generateNarration({ text: speech, engine: 'voxcpm' });
      onChange({
        ...segment,
        audio: {
          ...(segment.audio ?? {}),
          source: 'tts',
          src: res.src,
          devnagariText: res.devnagariText,
          audioForText: res.text,
          durationMs: res.durationMs,
          voiceModel: res.voiceModel,
        },
      });
    } catch (e) {
      setAudioErr(e instanceof Error ? e.message : 'Audio generation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-white p-2">
      <div className="flex items-center gap-1.5">
        <input
          value={segment.type ?? ''}
          onChange={(e) => onChange({ ...segment, type: e.target.value })}
          placeholder="type"
          className="h-7 w-24 rounded border border-border bg-white px-1.5 text-[11.5px] text-fg focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
        />
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="grid h-6 w-6 place-items-center rounded text-fg-subtle transition hover:bg-bg-subtle hover:text-fg"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        <button
          type="button"
          onClick={onRemove}
          className="grid h-6 w-6 place-items-center rounded text-fg-subtle transition hover:bg-bg-subtle hover:text-red-500"
          aria-label="Remove segment"
        >
          <Trash2 size={12} />
        </button>
      </div>

      <textarea
        value={segment.speech ?? ''}
        onChange={(e) => onChange({ ...segment, speech: e.target.value })}
        placeholder="What the tutor says…"
        rows={2}
        className="mt-1.5 w-full resize-y rounded border border-border bg-white px-2 py-1.5 text-[12px] text-fg focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
      />

      {/* Pre-baked narration audio for this segment (VoxCPM). */}
      <div className="mt-1.5 rounded border border-border-subtle bg-bg-subtle/40 p-1.5">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={generateAudio}
            disabled={busy || !speech}
            className="inline-flex shrink-0 items-center gap-1 rounded bg-accent-soft px-2 py-1 text-[11px] font-semibold text-accent transition hover:bg-accent-soft/70 disabled:opacity-50"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Volume2 size={11} />}
            {aud?.src ? 'Regenerate' : 'Generate audio'}
          </button>
          {aud?.src && (
            <audio controls src={aud.src} className="h-7 min-w-0 flex-1" />
          )}
        </div>
        {aud?.devnagariText && (
          <p className="mt-1 rounded bg-white px-1.5 py-1 text-[11px] leading-snug text-fg-muted">
            {aud.devnagariText}
          </p>
        )}
        {stale && (
          <p className="mt-1 text-[10.5px] text-amber-700">
            Speech changed — regenerate audio so it matches.
          </p>
        )}
        {audioErr && <p className="mt-1 text-[10.5px] text-red-600">{audioErr}</p>}
      </div>

      {expanded && (
        <div className="mt-1.5 space-y-1.5">
          <textarea
            value={segment.board ?? ''}
            onChange={(e) => onChange({ ...segment, board: e.target.value })}
            placeholder="What appears on the board (optional)"
            rows={2}
            className="w-full resize-y rounded border border-border bg-white px-2 py-1.5 text-[11.5px] text-fg focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20"
          />
          <div className="grid grid-cols-2 gap-1.5">
            <input
              value={segment.visualRef ?? ''}
              onChange={(e) => onChange({ ...segment, visualRef: e.target.value })}
              placeholder="visualRef"
              className={cn(
                'h-7 rounded border border-border bg-white px-1.5 text-[11px] text-fg',
                'focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20',
              )}
            />
            <input
              value={segment.imageUrl ?? ''}
              onChange={(e) => onChange({ ...segment, imageUrl: e.target.value })}
              placeholder="imageUrl"
              className={cn(
                'h-7 rounded border border-border bg-white px-1.5 text-[11px] text-fg',
                'focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/20',
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
